import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import StudentDetailModal from './StudentDetailModal';
import PDFExporter from './PDFExporter';
import SearchableDropdown from './SearchableDropdown';
import './DekanExamResults.css';
import API_BASE_URL, { API_ENDPOINTS } from '../config/api';

const DekanExamResults = () => {
    const { t } = useTranslation();
    const [facultyData, setFacultyData] = useState(null);
    const [selectedCourse, setSelectedCourse] = useState(null);
    const [courseResults, setCourseResults] = useState(null);
    const [commonClassAnalysis, setCommonClassAnalysis] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [pdfExamFilter, setPdfExamFilter] = useState('');
    const [outcomeExamFilter, setOutcomeExamFilter] = useState('');   // ÖÇ/PÇ tabloları için sınav filtresi
    const [studentExamFilter, setStudentExamFilter] = useState('');   // Öğrenci tablosu için sınav filtresi
    
    const [courseOutcomes, setCourseOutcomes] = useState([]);
    const [programOutcomes, setProgramOutcomes] = useState([]);
    const [questions, setQuestions] = useState([]);
    const [examWeights, setExamWeights] = useState([]);
    const [outcomeMappings, setOutcomeMappings] = useState({});
    const normalizeExamType = useCallback((t) => (t ? String(t).trim().toLowerCase() : ''), []);

    // Öğrenci detay modalı için state'ler
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [isStudentDetailModalOpen, setIsStudentDetailModalOpen] = useState(false);

    // Akademisyen id'sini sessionStorage'dan al
    const akademisyenId = sessionStorage.getItem('akademisyen_id');

    // Fakülte verilerini yükle
    const fetchFacultyData = useCallback(async () => {
        if (!akademisyenId) {
            setError('Akademisyen bilgisi bulunamadı');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const response = await axios.get(`${API_ENDPOINTS.DEKAN_EXAM_RESULTS}?akademisyen_id=${akademisyenId}`);
            console.log('Fakülte verileri:', response.data);
            setFacultyData(response.data);
        } catch (error) {
            console.error('Fakülte verileri yüklenirken hata:', error);
            setError(error.response?.data?.error || 'Fakülte verileri yüklenirken bir hata oluştu');
        } finally {
            setLoading(false);
        }
    }, [akademisyenId]);

    // Ders seçildiğinde detaylı sonuçları getir
    const fetchCourseResults = useCallback(async (courseId) => {
        if (!courseId) return;

        setLoading(true);
        setError('');

        try {
            const [response, commonResponse] = await Promise.all([
                axios.get(`${API_ENDPOINTS.DEKAN_COURSE_RESULTS(courseId)}?akademisyen_id=${akademisyenId}`),
                axios.get(`${API_BASE_URL}/api/pre-midterm/term-final-class-breakdown?course_id=${courseId}`)
            ]);
            console.log('Ders sonuçları:', response.data);
            setCourseResults(response.data);
            setCommonClassAnalysis(commonResponse.data);
            
            // Ders çıktılarını, soruları ve ağırlıkları da yükle
            await Promise.all([
                fetchCourseOutcomes(courseId),
                fetchQuestions(courseId),
                fetchExamWeights(courseId, response.data.course_info.program_id),
                fetchOutcomeMappings(response.data.course_info.program_id, courseId)
            ]);
        } catch (error) {
            setCommonClassAnalysis(null);
            console.error('Ders sonuçları yüklenirken hata:', error);
            setError(error.response?.data?.error || 'Ders sonuçları yüklenirken bir hata oluştu');
        } finally {
            setLoading(false);
        }
    }, [akademisyenId]);

    const fetchCourseOutcomes = async (courseId) => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/course-outcomes/${courseId}`);
            if (response.data && response.data.learning) {
                setCourseOutcomes(response.data.learning);
            } else {
                setCourseOutcomes([]);
            }
        } catch (error) {
            console.error('Ders çıktıları yüklenirken hata:', error);
            setCourseOutcomes([]);
        }
    };

    const fetchQuestions = async (courseId) => {
        try {
            const response = await axios.get(`${API_BASE_URL}/get-questions-by-course/${courseId}`);
            setQuestions(response.data);
        } catch (error) {
            console.error('Sorular yüklenirken hata:', error);
            setQuestions([]);
        }
    };

    const fetchExamWeights = async (courseId, programId) => {
        try {
            const response = await axios.get(`${API_BASE_URL}/get-exam-weights/${programId}/${courseId}`);
            if (response.data && response.data.weights) {
                setExamWeights(response.data.weights);
                // PDF için varsayılan sınav türü (ilk satır) – analiz tablolarını etkilemesin
                const firstType = response.data.weights[0]?.type || response.data.weights[0]?.exam_type || '';
                setPdfExamFilter(firstType);
            } else {
                setExamWeights([]);
                setPdfExamFilter('');
            }
        } catch (error) {
            console.error('Sınav ağırlıkları yüklenirken hata:', error);
            setExamWeights([]);
            setPdfExamFilter('');
        }
    };

    const fetchOutcomeMappings = async (programId, courseId) => {
        try {
            const response = await axios.get(`${API_BASE_URL}/get-outcome-mappings?program_id=${programId}&course_id=${courseId}`);
            if (response.data) {
                setOutcomeMappings(response.data);
            } else {
                setOutcomeMappings({});
            }
        } catch (error) {
            console.error('Öğrenme çıktısı eşleştirmeleri yüklenirken hata:', error);
            setOutcomeMappings({});
        }
    };

    // Program çıktılarını yükle
    const fetchProgramOutcomes = async (programId) => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/program-outcomes/${programId}`);
            if (response.data && response.data.outcomes) {
                setProgramOutcomes(response.data.outcomes);
            } else {
                setProgramOutcomes([]);
            }
        } catch (error) {
            console.error('Program çıktıları yüklenirken hata:', error);
            setProgramOutcomes([]);
        }
    };

    // Ders seçildiğinde
    const handleCourseSelect = (course) => {
        setSelectedCourse(course);
        fetchCourseResults(course.course_id);
        if (course.program_id) {
            fetchProgramOutcomes(course.program_id);
        }
    };

    // Genel çıktı oranlarını hesapla
    const calculateGeneralOutcomeRates = (students, examTypeOverride = outcomeExamFilter) => {
        if (commonClassAnalysis) {
            const typeKey = normalizeExamType(examTypeOverride);
            const averages = commonClassAnalysis.class_averages || {};
            const learning = typeKey
                ? (averages.per_type_learning_outcomes?.[typeKey] || [])
                : (averages.learning_outcomes || []);
            const program = typeKey
                ? (averages.per_type_program_outcomes?.[typeKey] || [])
                : (averages.program_outcomes || []);
            const convert = (item) => ({
                id: item.id,
                code: item.code,
                description: item.description,
                averageRate: Number(item.avg_pct || 0),
                studentCount: Number(item.student_count || 0)
            });
            return { learningRates: learning.map(convert), programRates: program.map(convert) };
        }
        // Ortak motor cevabı yoksa eski doğru-soru hesabına geri dönme.
        if (commonClassAnalysis === null) return { learningRates: [], programRates: [] };
        if (!students || students.length === 0) return { learningRates: [], programRates: [] };
        
        // Debug: İlk hesaplamada veri yapısını kontrol et
        if (students.length > 0 && questions.length > 0 && courseOutcomes.length > 0) {
            console.log('=== ÖÇ HESAPLAMA DEBUG ===');
            console.log('Course Outcomes:', courseOutcomes.map(oc => ({ id: oc.id, code: oc.code, type: typeof oc.id })));
            const questionsWithTopics = questions.filter(q => {
                const loIds = q.topics || q.learning_outcomes || [];
                return Array.isArray(loIds) && loIds.length > 0;
            });
            console.log(`Toplam ${questions.length} soru, ${questionsWithTopics.length} tanesi ÖÇ ile eşleştirilmiş`);
            if (questionsWithTopics.length === 0) {
                console.warn('⚠️ UYARI: Hiçbir soru ÖÇ ile eşleştirilmemiş! Soruları düzenleyip ÖÇ seçmeniz gerekiyor.');
            }
            console.log('Questions sample:', questions.slice(0, 3).map(q => ({
                id: q.id,
                topics: q.topics,
                learning_outcomes: q.learning_outcomes,
                topicsType: typeof q.topics,
                topicsIsArray: Array.isArray(q.topics),
                hasTopics: !!(q.topics && Array.isArray(q.topics) && q.topics.length > 0),
                topicsLength: Array.isArray(q.topics) ? q.topics.length : 0
            })));
            
            // İlk ÖÇ için eşleştirme kontrolü
            if (courseOutcomes.length > 0 && questions.length > 0) {
                const firstOutcome = courseOutcomes[0];
                const matchingQuestions = questions.filter(q => {
                    const loIds = q.topics || q.learning_outcomes || [];
                    if (!Array.isArray(loIds)) return false;
                    return loIds.some(loId => String(loId) === String(firstOutcome.id));
                });
                console.log(`İlk ÖÇ (${firstOutcome.code}, id: ${firstOutcome.id}) için eşleşen soru sayısı: ${matchingQuestions.length}`);
                if (matchingQuestions.length > 0) {
                    console.log('Eşleşen sorular:', matchingQuestions.map(q => ({
                        id: q.id,
                        topics: q.topics,
                        learning_outcomes: q.learning_outcomes
                    })));
                }
            }
        }
        
        const allStudentOutcomes = students.map(student => {
            const studentAnswers = student.answer_key || [];
            
            // ÖÇ analizi - Backend mantığına uygun: büyük harf = doğru, küçük harf = yanlış
            const learningOutcomeResults = Array.isArray(courseOutcomes) ? courseOutcomes.map((outcome) => {
                const relatedQuestionIndexes = questions
                    .map((q, idx) => {
                        // topics alanını kontrol et - hem topics hem learning_outcomes destekle
                        const loIds = q.topics || q.learning_outcomes || [];
                        if (!Array.isArray(loIds) || loIds.length === 0) return -1;
                        // ID'leri string'e çevirip karşılaştır
                        const hasMatch = loIds.some(loId => String(loId) === String(outcome.id));
                        return hasMatch ? idx : -1;
                    })
                    .filter(idx => idx !== -1);
                
                // İlgili soruların cevaplarını al
                const relatedAnswers = relatedQuestionIndexes.map(idx => studentAnswers[idx]);
                
                // Backend mantığı: büyük harf = doğru (isupper()), küçük harf = yanlış, boş = boş
                const correctCount = relatedAnswers.filter(ans => {
                    if (!ans) return false; // Boş cevaplar doğru değil
                    const ansStr = String(ans);
                    return ansStr === ansStr.toUpperCase() && ansStr !== ansStr.toLowerCase();
                }).length;
                
                const totalCount = relatedAnswers.length;
                const successRate = totalCount > 0 ? (correctCount / totalCount) * 100 : 0;
                
                return {
                    id: outcome.id,
                    code: outcome.code,
                    description: outcome.description,
                    correctCount,
                    totalCount,
                    successRate: Number(successRate.toFixed(2))
                };
            }) : [];

            // PÇ analizi - Backend mantığına uygun: hem doğrudan program_outcomes hem de outcome_mappings kullanılır
            const programOutcomeResults = Array.isArray(programOutcomes) ? programOutcomes.map((outcome) => {
                const relatedQuestionIndexes = new Set();
                
                // 1. Sorunun doğrudan program_outcomes'undan eşleşen soruları bul
                questions.forEach((q, idx) => {
                    if (q.program_outcomes && q.program_outcomes.map(String).includes(String(outcome.id))) {
                        relatedQuestionIndexes.add(idx);
                    }
                });
                
                // 2. Sorunun learning_outcomes'undan outcome_mappings ile eşleşen soruları bul
                questions.forEach((q, idx) => {
                    if (q.topics && Array.isArray(q.topics)) {
                        q.topics.forEach(loId => {
                            const mappedPcs = outcomeMappings[String(loId)] || [];
                            if (mappedPcs.includes(outcome.id)) {
                                relatedQuestionIndexes.add(idx);
                            }
                        });
                    }
                });
                
                const relatedAnswers = Array.from(relatedQuestionIndexes).map(idx => studentAnswers[idx]);
                const correctCount = relatedAnswers.filter(ans => ans && ans === ans.toUpperCase() && ans !== ans.toLowerCase()).length;
                const totalCount = relatedAnswers.length;
                const successRate = totalCount > 0 ? (correctCount / totalCount) * 100 : 0;
                
                return {
                    id: outcome.id,
                    code: outcome.code,
                    description: outcome.description,
                    correctCount,
                    totalCount,
                    successRate: Number(successRate.toFixed(2))
                };
            }) : [];

            return {
                learningOutcomes: learningOutcomeResults,
                programOutcomes: programOutcomeResults
            };
        });
        
        // ÖÇ genel oranları
        const generalLearningRates = Array.isArray(courseOutcomes) ? courseOutcomes.map(outcome => {
            const allRates = allStudentOutcomes.map(studentOutcome => {
                const outcomeData = studentOutcome.learningOutcomes.find(oc => oc.id === outcome.id);
                return outcomeData ? outcomeData.successRate : 0;
            }).filter(rate => rate > 0);
            
            const averageRate = allRates.length > 0 
                ? allRates.reduce((sum, rate) => sum + rate, 0) / allRates.length 
                : 0;
            
            return {
                id: outcome.id,
                code: outcome.code,
                description: outcome.description,
                averageRate: Number(averageRate.toFixed(2)),
                studentCount: allRates.length
            };
        }) : [];

        // PÇ genel oranları
        const generalProgramRates = Array.isArray(programOutcomes) ? programOutcomes.map(outcome => {
            const allRates = allStudentOutcomes.map(studentOutcome => {
                const outcomeData = studentOutcome.programOutcomes.find(pc => pc.id === outcome.id);
                return outcomeData ? outcomeData.successRate : 0;
            }).filter(rate => rate > 0);
            
            const averageRate = allRates.length > 0 
                ? allRates.reduce((sum, rate) => sum + rate, 0) / allRates.length 
                : 0;
            
            return {
                id: outcome.id,
                code: outcome.code,
                description: outcome.description,
                averageRate: Number(averageRate.toFixed(2)),
                studentCount: allRates.length
            };
        }) : [];

        return {
            learningRates: generalLearningRates,
            programRates: generalProgramRates
        };
    };

    // Sınav türüne göre öğrencileri filtrele
    const filterStudentsByExamType = useCallback((students, examTypeOverride) => {
        const activeType = examTypeOverride !== undefined ? examTypeOverride : pdfExamFilter;
        if (!activeType) return students || [];
        const target = normalizeExamType(activeType);
        return (students || []).filter(s => normalizeExamType(s.exam_type || s.examType) === target);
    }, [pdfExamFilter, normalizeExamType]);

    // Öğrenci detay modalı için veri hazırlama
    const prepareStudentDataForModal = useCallback((student) => {
        if (!student || !questions) return null;

        const studentAnswers = student.answer_key || [];
        const totalQuestionCount = studentAnswers.length;
        const pointsPerQuestion = totalQuestionCount > 0 ? 100 / totalQuestionCount : 0;
        
        // Soru analizi hazırla
        const questionAnalysis = questions.map((question, index) => {
            const studentAnswer = studentAnswers[index] || 'Boş';
            const correctAnswer = question.correct_answer || '';
            const isCorrect = studentAnswer && studentAnswer.toUpperCase() === correctAnswer.toUpperCase();
            
            return {
                answer: studentAnswer,
                isCorrect: isCorrect,
                questionNumber: index + 1,
                questionText: question.text || `Soru ${index + 1}`,
                correctAnswer: correctAnswer,
                points: isCorrect ? pointsPerQuestion : 0
            };
        });

        // Genel istatistikler
        const correctAnswers = studentAnswers.filter(ans => ans && ans.toUpperCase() === ans && ans !== ans.toLowerCase()).length;
        const wrongAnswers = studentAnswers.filter(ans => ans && ans !== ans.toUpperCase() && ans !== ans.toLowerCase()).length;
        const emptyAnswers = totalQuestionCount - correctAnswers - wrongAnswers;
        const successRate = totalQuestionCount > 0 ? (correctAnswers / totalQuestionCount) * 100 : 0;
        const totalPoints = correctAnswers * pointsPerQuestion;

        // Öğrenme çıktıları analizi
        const learningOutcomeResults = Array.isArray(courseOutcomes) ? courseOutcomes.map((outcome) => {
            const relatedQuestionIndexes = questions
                .map((q, idx) => (q.topics && q.topics.map(String).includes(String(outcome.id)) ? idx : -1))
                .filter(idx => idx !== -1);
            
            const relatedAnswers = relatedQuestionIndexes.map(idx => studentAnswers[idx]);
            const correctCount = relatedAnswers.filter(ans => ans && ans.toUpperCase() === ans && ans !== ans.toLowerCase()).length;
            const totalCount = relatedAnswers.length;
            const successRate = totalCount > 0 ? (correctCount / totalCount) * 100 : 0;
            
            const relatedQuestions = relatedQuestionIndexes.map((idx, qIdx) => {
                const question = questions[idx];
                const answer = studentAnswers[idx];
                const isCorrect = answer && answer.toUpperCase() === answer && answer !== answer.toLowerCase();
                
                return {
                    questionNumber: idx + 1,
                    questionText: question.text || `Soru ${idx + 1}`,
                    studentAnswer: answer || 'Boş',
                    isCorrect: isCorrect,
                    correctAnswer: question.correct_answer || 'N/A',
                    questionIndex: idx
                };
            });
            
            return {
                id: outcome.id,
                code: outcome.code,
                description: outcome.description,
                correctCount,
                totalCount,
                successRate: Number(successRate.toFixed(2)),
                relatedQuestions
            };
        }) : [];

        // Program çıktıları analizi
        const programOutcomeResults = Array.isArray(programOutcomes) ? programOutcomes.map((outcome) => {
            const relatedQuestionIndexes = questions
                .map((q, idx) => (q.program_outcomes && q.program_outcomes.map(String).includes(String(outcome.id)) ? idx : -1))
                .filter(idx => idx !== -1);
            
            const relatedAnswers = relatedQuestionIndexes.map(idx => studentAnswers[idx]);
            const correctCount = relatedAnswers.filter(ans => ans && ans.toUpperCase() === ans && ans !== ans.toLowerCase()).length;
            const totalCount = relatedAnswers.length;
            const successRate = totalCount > 0 ? (correctCount / totalCount) * 100 : 0;
            
            const relatedQuestions = relatedQuestionIndexes.map((idx, qIdx) => {
                const question = questions[idx];
                const answer = studentAnswers[idx];
                const isCorrect = answer && answer.toUpperCase() === answer && answer !== answer.toLowerCase();
                
                return {
                    questionNumber: idx + 1,
                    questionText: question.text || `Soru ${idx + 1}`,
                    studentAnswer: answer || 'Boş',
                    isCorrect: isCorrect,
                    correctAnswer: question.correct_answer || 'N/A',
                    questionIndex: idx
                };
            });
            
            return {
                id: outcome.id,
                code: outcome.code,
                description: outcome.description,
                correctCount,
                totalCount,
                successRate: Number(successRate.toFixed(2)),
                relatedQuestions
            };
        }) : [];

        return {
            studentInfo: [
                null, // index 0
                student.student_no || 'N/A', // index 1
                student.student_name || 'N/A', // index 2
                null, // index 3
                student.exam_type || 'N/A' // index 4
            ],
            questionAnalysis: questionAnalysis,
            dc: correctAnswers,
            yc: wrongAnswers,
            bos: emptyAnswers,
            dcPercent: successRate,
            totalPoints: totalPoints,
            totalQuestionCount: totalQuestionCount,
            pointsPerQuestion: pointsPerQuestion,
            learningOutcomeResults: learningOutcomeResults,
            programOutcomeResults: programOutcomeResults
        };
    }, [questions, courseOutcomes, programOutcomes]);

    // Sayfa yüklendiğinde fakülte verilerini getir
    useEffect(() => {
        fetchFacultyData();
    }, [fetchFacultyData]);

    if (loading && !facultyData) {
        return (
            <div className="dekan-exam-results">
                <div className="loading-section">
                    <div className="loading-spinner"></div>
                    <p>Fakülte verileri yükleniyor...</p>
                </div>
            </div>
        );
    }

    if (error && !facultyData) {
        return (
            <div className="dekan-exam-results">
                <div className="error-section">
                    <div className="error-message">
                        {error}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="dekan-exam-results">
            <div className="page-header">
                <h2>{t('examResults.dekanTitle')}</h2>
                <p>{facultyData?.faculty_name} fakültesine ait tüm sınav sonuçları</p>
            </div>

            <div className="content-container">
                {/* Sol taraf - Ders listesi */}
                <div className="courses-section">
                    <div className="courses-header">
                        <h3>Dersler</h3>
                        <span className="course-count">Toplam: {facultyData?.total_courses || 0} ders</span>
                    </div>
                    
                    <div className="courses-list">
                        {facultyData?.courses?.map((course, index) => (
                            <div 
                                key={index}
                                className={`course-item ${selectedCourse?.course_id === course.course_id ? 'selected' : ''}`}
                                onClick={() => handleCourseSelect(course)}
                            >
                                <div className="course-info">
                                    <h4>{course.course_name}</h4>
                                    <p className="program-name">{course.program_name}</p>
                                </div>
                                <div className="course-stats">
                                    <span className="exam-count">{course.exam_count} sınav</span>
                                    <span className="student-count">{course.total_students} öğrenci</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Sağ taraf - Seçili dersin sonuçları */}
                <div className="results-section">
                    {selectedCourse ? (
                        <>
                            <div className="selected-course-header">
                                <h3>{selectedCourse.course_name}</h3>
                                <p>{selectedCourse.program_name}</p>
                            </div>

                            {loading ? (
                                <div className="loading-section">
                                    <div className="loading-spinner"></div>
                                    <p>Ders sonuçları yükleniyor...</p>
                                </div>
                            ) : courseResults ? (
                                <>
                                    {/* Sınav Sonuçları Analizi */}
                                    {(() => {
                                        // ÖÇ/PÇ tabloları için seçilen sınav türüne göre filtrele
                                        const outcomeFilteredStudents = filterStudentsByExamType(
                                            courseResults.students,
                                            outcomeExamFilter
                                        );
                                        const generalRates = calculateGeneralOutcomeRates(outcomeFilteredStudents);

                                        // Öğrenci tablosu için ayrı filtre
                                        const studentFilteredStudents = filterStudentsByExamType(
                                            courseResults.students,
                                            studentExamFilter
                                        );
                                        
                                        return (
                                            <div className="outcome-analysis">
                                                <h4>Sınav Sonuçları Analizi</h4>

                                                {/* ÖÇ / PÇ tabloları için sınav türü filtresi */}
                                                {examWeights.length > 0 && (
                                                    <div className="exam-filter-row">
                                                        <label>ÖÇ / PÇ için sınav türü:</label>
                                                        <SearchableDropdown
                                                            value={outcomeExamFilter}
                                                            onChange={setOutcomeExamFilter}
                                                            placeholder="Tümü"
                                                            searchPlaceholder="Sınav türü ara…"
                                                            ariaLabel="ÖÇ ve PÇ sınav türü"
                                                            options={examWeights.map((weight, index) => {
                                                                const typeLabel = weight.type || weight.exam_type;
                                                                return {
                                                                    value: typeLabel || `weight-${index}`,
                                                                    label: `${typeLabel} (${weight.weight}%)`,
                                                                    searchText: typeLabel || '',
                                                                };
                                                            })}
                                                        />
                                                    </div>
                                                )}

                                                {/* Genel ÖÇ Oranları */}
                                                {generalRates.learningRates.length > 0 && (
                                                    <div className="summary-table-container">
                                                        <h4>Genel Öğrenme Çıktıları (ÖÇ) Başarı Oranları</h4>
                                                        <div className="table-scroll">
                                                            <table className="student-outcome-summary-table">
                                                                <thead>
                                                                    <tr>
                                                                        <th>ÖÇ Kodu</th>
                                                                        <th>Açıklama</th>
                                                                        <th>Genel Başarı Oranı</th>
                                                                        <th>Öğrenci Sayısı</th>
                                                                        <th>Durum</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {generalRates.learningRates.map((outcome, index) => (
                                                                        <tr key={index}>
                                                                            <td><strong>{outcome.code || `ÖÇ${outcome.id}`}</strong></td>
                                                                            <td>{outcome.description}</td>
                                                                            <td>
                                                                                <span className={`badge ${outcome.averageRate >= 60 ? 'success' : 'fail'}`}>
                                                                                    {outcome.averageRate}%
                                                                                </span>
                                                                            </td>
                                                                            <td>
                                                                                <span className="student-count">{outcome.studentCount}</span>
                                                                            </td>
                                                                            <td>
                                                                                <span className={`status ${outcome.averageRate >= 60 ? 'success' : 'fail'}`}>
                                                                                    {outcome.averageRate >= 60 ? 'Başarılı' : 'Başarısız'}
                                                                                </span>
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Genel PÇ Oranları */}
                                                {generalRates.programRates.length > 0 && (
                                                    <div className="summary-table-container">
                                                        <h4>Genel Program Çıktıları (PÇ) Başarı Oranları</h4>
                                                        <div className="table-scroll">
                                                            <table className="student-outcome-summary-table">
                                                                <thead>
                                                                    <tr>
                                                                        <th>PÇ Kodu</th>
                                                                        <th>Açıklama</th>
                                                                        <th>Genel Başarı Oranı</th>
                                                                        <th>Öğrenci Sayısı</th>
                                                                        <th>Durum</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {generalRates.programRates.map((outcome, index) => (
                                                                        <tr key={index}>
                                                                            <td><strong>{outcome.code || `PÇ${outcome.id}`}</strong></td>
                                                                            <td>{outcome.description}</td>
                                                                            <td>
                                                                                <span className={`badge ${outcome.averageRate >= 60 ? 'success' : 'fail'}`}>
                                                                                    {outcome.averageRate}%
                                                                                </span>
                                                                            </td>
                                                                            <td>
                                                                                <span className="student-count">{outcome.studentCount}</span>
                                                                            </td>
                                                                            <td>
                                                                                <span className={`status ${outcome.averageRate >= 60 ? 'success' : 'fail'}`}>
                                                                                    {outcome.averageRate >= 60 ? 'Başarılı' : 'Başarısız'}
                                                                                </span>
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Öğrenci Bazlı Liste */}
                                                {examWeights.length > 0 && (
                                                    <div className="exam-filter-row">
                                                        <label>Öğrenci tablosu için sınav türü:</label>
                                                        <SearchableDropdown
                                                            value={studentExamFilter}
                                                            onChange={setStudentExamFilter}
                                                            placeholder="Tümü"
                                                            searchPlaceholder="Sınav türü ara…"
                                                            ariaLabel="Öğrenci tablosu sınav türü"
                                                            options={examWeights.map((weight, index) => {
                                                                const typeLabel = weight.type || weight.exam_type;
                                                                return {
                                                                    value: typeLabel || `weight-${index}`,
                                                                    label: `${typeLabel} (${weight.weight}%)`,
                                                                    searchText: typeLabel || '',
                                                                };
                                                            })}
                                                        />
                                                    </div>
                                                )}

                                                <div className="summary-table-container">
                                                    <h4>Öğrenci Bazlı Sınav Sonuçları</h4>
                                                    <div className="table-scroll">
                                                        <table className="student-outcome-summary-table">
                                                            <thead>
                                                                <tr>
                                                                    <th>Öğrenci No</th>
                                                                    <th>Ad Soyad</th>
                                                                    <th>Sınav Türü</th>
                                                                    <th>Toplam Soru</th>
                                                                    <th>Doğru Sayısı</th>
                                                                    <th>Yanlış Sayısı</th>
                                                                    <th>Boş Sayısı</th>
                                                                    <th>Başarı Oranı</th>
                                                                    <th>Durum</th>
                                                                </tr>
                                                            </thead>
                                                                <tbody>
                                                                {studentFilteredStudents.map((student, index) => {
                                                                    const studentAnswers = student.answer_key || [];
                                                                    const totalQuestions = studentAnswers.length;
                                                                    const correctAnswers = studentAnswers.filter(ans => ans && ans === ans.toUpperCase() && ans !== ans.toLowerCase()).length;
                                                                    const wrongAnswers = studentAnswers.filter(ans => ans && ans !== ans.toUpperCase() && ans !== ans.toLowerCase()).length;
                                                                    const emptyAnswers = totalQuestions - correctAnswers - wrongAnswers;
                                                                    const successRate = totalQuestions > 0 ? (correctAnswers / totalQuestions) * 100 : 0;
                                                                    
                                                                    return (
                                                                        <tr key={index} onClick={() => {
                                                                            setSelectedStudent(student);
                                                                            setIsStudentDetailModalOpen(true);
                                                                        }} style={{ cursor: 'pointer' }}>
                                                                            <td><strong>{student.student_no || 'N/A'}</strong></td>
                                                                            <td>{student.student_name || 'N/A'}</td>
                                                                            <td>{student.exam_type || 'N/A'}</td>
                                                                            <td>{totalQuestions}</td>
                                                                            <td>
                                                                                <span className="correct-count">{correctAnswers}</span>
                                                                            </td>
                                                                            <td>
                                                                                <span className="wrong-count">{wrongAnswers}</span>
                                                                            </td>
                                                                            <td>
                                                                                <span className="empty-count">{emptyAnswers}</span>
                                                                            </td>
                                                                            <td>
                                                                                <span className={`badge ${successRate >= 60 ? 'success' : 'fail'}`}>
                                                                                    {successRate.toFixed(2)}%
                                                                                </span>
                                                                            </td>
                                                                            <td>
                                                                                <span className={`status ${successRate >= 60 ? 'success' : 'fail'}`}>
                                                                                    {successRate >= 60 ? 'Başarılı' : 'Başarısız'}
                                                                                </span>
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })()}

                                    {/* PDF İndirme Butonu en altta */}
                                    <PDFExporter
                                        filteredResults={courseResults}
                                        courseOutcomes={courseOutcomes}
                                        programOutcomes={programOutcomes}
                                        questions={questions}
                                        selectedExamTypeForAnalysis={pdfExamFilter}
                                        selectedEducationType=""
                                        selectedFaculty={facultyData?.faculty_id}
                                        selectedProgram={courseResults.course_info.program_id}
                                        selectedCourse={courseResults.course_info.course_id}
                                        educationTypes={[]}
                                        faculties={[{ id: facultyData?.faculty_id, name: facultyData?.faculty_name }]}
                                        programs={[{ id: courseResults.course_info.program_id, name: courseResults.course_info.program_name }]}
                                        courses={[{ id: courseResults.course_info.course_id, name: courseResults.course_info.course_name }]}
                                        weights={examWeights}
                                        generalRates={calculateGeneralOutcomeRates(
                                            filterStudentsByExamType(courseResults.students, pdfExamFilter),
                                            pdfExamFilter
                                        )}
                                    />
                                </>
                            ) : (
                                <div className="no-results">
                                    <p>Bu ders için henüz sınav sonucu bulunmamaktadır.</p>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="no-selection">
                            <p>Görüntülemek için bir ders seçin</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Öğrenci Detay Modalı */}
            {selectedStudent && (
                <StudentDetailModal
                    isOpen={isStudentDetailModalOpen}
                    onClose={() => {
                        setIsStudentDetailModalOpen(false);
                        setSelectedStudent(null);
                    }}
                    studentData={prepareStudentDataForModal(selectedStudent)}
                    courseOutcomes={courseOutcomes}
                    programOutcomes={programOutcomes}
                    questions={questions}
                    outcomeMappings={outcomeMappings}
                    courseId={selectedCourse?.course_id}
                    programId={courseResults?.course_info?.program_id}
                />
            )}
        </div>
    );
};

export default DekanExamResults;
